import { Button } from "@/components/ui/button";
import { LogOut, Menu, Settings, MessageSquare, User, ChevronDown, Trophy, Star, CreditCard } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import { useNewUnverifiedMembers } from "@/hooks/useNewUnverifiedMembers";
import { useScrollDirection } from "@/hooks/useScrollDirection";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { UserAvatar } from "./UserAvatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { getPlayerStats, getCreatorStats, getDeveloperStats, type PlayerStats } from "@/services/pointsService";
import logo from "@/assets/logo.png";
import { Gems } from "./Gems";
import { UserSideMenu } from "./UserSideMenu";

export const Header = () => {
  const { signOut, user } = useAuth();
  const { unreadCount } = useUnreadMessages();
  const { hasNewUnverifiedMembers } = useNewUnverifiedMembers();
  const { scrollDirection, isAtTop } = useScrollDirection();
  const navigate = useNavigate();
  const location = useLocation();
  const [userType, setUserType] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [playerStats, setPlayerStats] = useState<PlayerStats | null>(null);
  const [creatorStats, setCreatorStats] = useState<any>(null);
  const [developerStats, setDeveloperStats] = useState<any>(null);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);

  const fetchUserProfile = async (isSilent = false) => {
    if (user) {
      if (!isSilent) setIsProfileLoading(true);
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select(`
            id, user_type, display_name, avatar_url, is_admin, is_premium, 
            premium_end_date, active_border_id, 
            active_border:shop_items!profiles_active_border_id_fkey(metadata), 
            boost_end_date
          `)
          .eq("id", user.id)
          .single();

        if (error) {
          console.error("Error fetching user profile in Header:", error);
          return;
        }

        setUserType(data?.user_type || null);

        // Extract border style safely (can be metadata.style or metadata.image_url)
        const borderData = Array.isArray(data.active_border) ? data.active_border[0] : data.active_border;
        const resolvedStyle = borderData?.metadata?.style || borderData?.metadata?.image_url;

        const normalizedProfile = {
          ...data,
          resolvedBorderStyle: resolvedStyle
        };

        setUserProfile(normalizedProfile);

        // Load stats based on user type
        if (data?.user_type === 'player') {
          try {
            const stats = await getPlayerStats(user.id);
            setPlayerStats(stats);
          } catch (error) {
            // Silent error
          }
        } else if (data?.user_type === 'creator') {
          try {
            const [stats, { count }] = await Promise.all([
              getCreatorStats(user.id),
              supabase
                .from('key_requests')
                .select('*', { count: 'exact', head: true })
                .eq('creator_id', user.id)
                .eq('status', 'pending')
            ]);
            setCreatorStats(stats);
            if (count !== null) setPendingRequestsCount(count);
          } catch (error) {
            console.error("Error fetching creator stats:", error);
          }
        } else if (data?.user_type === 'developer') {
          try {
            const stats = await getDeveloperStats(user.id);
            setDeveloperStats(stats);
          } catch (error) {
            console.error("Error fetching developer stats:", error);
          }
        }
      } catch (error) {
        console.error("Unexpected error in Header fetchUserProfile:", error);
      } finally {
        if (!isSilent) setIsProfileLoading(false);
      }
    } else {
      setIsProfileLoading(false);
    }
  };

  useEffect(() => {
    let channel: any;

    const handleProfileUpdate = () => fetchUserProfile(true);

    if (user?.id) {
      fetchUserProfile();

      // Listen for local events
      window.addEventListener('profile-updated', handleProfileUpdate);

      // Listen for real-time profile changes
      channel = supabase
        .channel(`header_profile_${user.id}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'profiles',
            filter: `id=eq.${user.id}`,
          },
          () => {
            handleProfileUpdate();
          }
        )
        .subscribe();
    } else {
      setIsProfileLoading(false);
      setUserType(null);
      setUserProfile(null);
      setPlayerStats(null);
      setCreatorStats(null);
      setDeveloperStats(null);
      setPendingRequestsCount(0);
    }

    return () => {
      window.removeEventListener('profile-updated', handleProfileUpdate);
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [user?.id]);

  const handleLogoClick = () => {
    if (userType) {
      navigate("/dashboard");
    } else {
      navigate("/");
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const getMenuItems = () => {
    const baseItems = [
      { label: "Dashboard", path: "/dashboard" },
      { label: "Key Requests", path: "/key-requests" },
      { label: "Coverage", path: "/coverage" },
      { label: "Beta Ideas", path: "/beta-ideas" },
    ];

    // Check if user is admin (must be checked before any returns)
    const isAdmin = userProfile?.is_admin || userProfile?.display_name === 'miikeu';

    // Add "Browse Games" for creators
    if (userType === 'creator') {
      baseItems.splice(1, 0, { label: "Browse Games", path: "/games" });
      baseItems.splice(2, 0, { label: "My Profile", path: "/creator-profile" });
      // Remove "Key Requests" and "Coverage" for creators (available in Creator Profile)
      const creatorItems = baseItems.filter(item =>
        item.path !== '/key-requests' && item.path !== '/coverage'
      );
      // Add "Admin" for admin creators
      if (isAdmin) {
        creatorItems.push({ label: "Admin", path: "/admin" });
      }
      return creatorItems;
    }

    // Add "Subscriptions" and "Statistics" for developers
    if (userType === 'developer') {
      baseItems.splice(3, 0, { label: "Subscriptions", path: "/subscriptions" });

      // Vérifier le statut premium pour afficher "Statistics"
      const hasPremium = userProfile?.is_premium === true;
      const premiumEndDate = userProfile?.premium_end_date;
      let isPremiumActive = false;

      if (hasPremium) {
        // Si pas de date de fin, c'est premium à vie
        if (!premiumEndDate) {
          isPremiumActive = true;
        } else {
          // Vérifier que la date n'est pas expirée
          const endDate = new Date(premiumEndDate);
          const now = new Date();
          isPremiumActive = endDate > now;
        }
      }

      // Ajouter "Statistics" seulement si premium actif
      if (isPremiumActive) {
        baseItems.splice(4, 0, { label: "Statistics", path: "/statistics" });
      }

      // Keep "Key Requests" for developers, but remove "Coverage" (only for creators in profile)
      const developerItems = baseItems.filter(item => item.path !== '/coverage');
      // Add "Admin" for admin developers
      if (isAdmin) {
        developerItems.push({ label: "Admin", path: "/admin" });
      }
      return developerItems;
    }

    // Customize menu for players
    if (userType === 'player') {
      // console.log('Player detected, filtering menu items');
      // Remove Coverage and Key Requests for players
      const playerItems = baseItems.filter(item =>
        item.path !== '/coverage' && item.path !== '/key-requests'
      );
      // Add My Profile (redirects to player profile)
      playerItems.splice(1, 0, { label: "My Profile", path: "/player-profile" });
      // Add Browse Games
      playerItems.splice(1, 0, { label: "Browse Games", path: "/games" });
      // Add "Admin" for admin players
      if (isAdmin) {
        playerItems.push({ label: "Admin", path: "/admin" });
      }
      // console.log('Player menu items:', playerItems);
      return playerItems;
    }

    // Add "Admin" for admin users (fallback to display_name check)
    if (isAdmin) {
      baseItems.push({ label: "Admin", path: "/admin" });
    }

    // console.log('Non-player menu items:', baseItems);
    return baseItems;
  };

  const menuItems = getMenuItems();

  // Debug: log userType
  // console.log('Current userType:', userType);

  // If user is not logged in, show only logo
  if (!user) {
    return (
      <header className={`border-b border-border/50 bg-card/30 backdrop-blur sticky top-0 z-50 transition-transform duration-300 ${scrollDirection === 'down' && !isAtTop ? '-translate-y-full' : 'translate-y-0'
        }`}>
        <div className="container mx-auto px-4 py-4 flex justify-center items-center">
          <div
            className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={handleLogoClick}
          >
            <img src={logo} alt="IndieKeys" className="h-10" />
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className={`border-b border-border/50 bg-card/30 backdrop-blur sticky top-0 z-50 transition-transform duration-300 ${scrollDirection === 'down' && !isAtTop ? '-translate-y-full' : 'translate-y-0'
      }`}>
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        {/* Logo */}
        <div
          className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity shrink min-w-0"
          onClick={handleLogoClick}
        >
          <img src={logo} alt="IndieKeys" className="h-8 sm:h-10 object-contain" />
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-6">
          {menuItems.map((item) => (
            <button
              key={item.path}
              onClick={() => handleNavigation(item.path)}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors relative"
            >
              {item.label}
              {item.path === "/admin" && hasNewUnverifiedMembers && (
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              )}
            </button>
          ))}
        </nav>

        {/* Desktop Profile Menu */}
        <div className="hidden md:flex items-center gap-4">
          {isProfileLoading ? (
            <div className="flex items-center gap-2 p-2 relative bg-card rounded-custom-paddings">
              <div className="w-8 h-8 bg-muted rounded-full animate-pulse"></div>
              <div className="text-left">
                <div className="w-16 h-4 bg-muted rounded animate-pulse mb-1"></div>
                <div className="w-12 h-3 bg-muted rounded animate-pulse"></div>
              </div>
              <div className="w-4 h-4 bg-muted rounded animate-pulse"></div>
            </div>
          ) : userProfile ? (
            <Sheet open={isUserMenuOpen} onOpenChange={setIsUserMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 p-2 relative bg-card hover:bg-orange-50 ">
                  <UserAvatar
                    src={userProfile?.avatar_url || undefined}
                    fallback={userProfile?.display_name || "U"}
                    size="sm"
                    borderStyle={userProfile?.resolvedBorderStyle}
                  />
                  {unreadCount > 0 && (
                    <div className="absolute left-1 top-0 w-3 h-3 bg-red-500 rounded-full border-2 border-background"></div>
                  )}
                  <div className="text-left">
                    <div className="text-sm font-medium">{userProfile?.display_name}</div>
                    <div className="text-xs text-muted-foreground capitalize">
                      {userProfile?.user_type === 'player' && playerStats ? (
                        <>
                          Lvl {playerStats.player_level} • {playerStats.coins} <Gems size={10} with_text={false} />
                        </>
                      ) : (
                        <>
                          {userProfile?.user_type}
                          {userProfile?.user_type === 'creator' && userProfile?.platform && (
                            <span className="ml-1">• {userProfile.platform}</span>
                          )}
                          {userProfile?.user_type === 'developer' && userProfile?.studio_name && (
                            <span className="ml-1">• {userProfile.studio_name}</span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full p-0 border-none overflow-hidden max-w-full sm:max-w-[500px]">
                <UserSideMenu
                  userProfile={userProfile}
                  playerStats={playerStats}
                  creatorStats={creatorStats}
                  developerStats={developerStats}
                  pendingRequestsCount={pendingRequestsCount}
                  signOut={signOut}
                  onClose={() => setIsUserMenuOpen(false)}
                />
              </SheetContent>
            </Sheet>
          ) : (
            <Button variant="outline" onClick={signOut}>
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          )}
        </div>

        {/* Mobile Menu */}
        <div className="md:hidden">
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full p-0 border-none overflow-hidden max-w-full">
              <UserSideMenu
                userProfile={userProfile}
                playerStats={playerStats}
                signOut={signOut}
                onClose={() => setIsMobileMenuOpen(false)}
                navItems={menuItems}
              />
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header >
  );
};
